import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./InstructionsPage.css";

const QUICK_ACTIONS = [
    {
        title: "Upload archive record",
        description: "Add a finished PDF, Word file, image, summons, agenda, minutes, report, photo, warrant, or supporting file.",
        route: "/upload-documents",
        cta: "Open Uploads",
    },
    {
        title: "Review archive records",
        description: "Find documents by lodge, date, type, uploader, access role, review status, title, description, or exact ID.",
        route: "/view-documents",
        cta: "Open Archive",
    },
    {
        title: "Process email staging",
        description: "Inspect captured email attachments, correct parsed metadata, validate good records, and upload them to the archive.",
        route: "/staging-imports",
        cta: "Open Staging",
    },
    {
        title: "Manage people and lodges",
        description: "Maintain member profiles, lodge records, membership history, degrees, offices, contact details, photos, and dues.",
        route: "/resources",
        cta: "Open Resources",
    },
    {
        title: "Create RSVP survey",
        description: "Create meeting attendance surveys, set the response token, invite/result email recipients, and review responses.",
        route: "/surveys",
        cta: "Open Surveys",
    },
    {
        title: "Publish public heritage",
        description: "Review public-safe archive items, publish lodge bodies and featured items, and keep protected detail behind login.",
        route: "/research",
        cta: "Open Research",
    },
    {
        title: "Manage admin queues",
        description: "Update users, reference data, lookup lists, lodge suggestions, and other controlled settings.",
        route: "/admin",
        cta: "Open Admin",
    },
    {
        title: "Check analytics",
        description: "Use archive, member, download, visitor, location, and quality reports to find gaps and follow-up work.",
        route: "/analytics",
        cta: "Open Analytics",
    },
];

const WORKFLOWS = [
    {
        id: "upload",
        title: "Upload a finished archive item",
        owner: "Secretary or Archivist",
        time: "4-8 min",
        steps: [
            "Open Upload Documents and select the correct lodge or body, constitution or order, country, document type, event type, file type, and date.",
            "Use the generated title as a starting point, then make it readable enough for a person scanning search results.",
            "Write a short factual description. For minutes, agendas, reports, and financial packs, mention only the important content that is actually present.",
            "Set version, addendum, author, DOI, and meeting date only when those fields apply to the record.",
            "Choose the correct access roles. Public-safe heritage items can be queued for public review; member-level, private, financial, and personal material should stay protected.",
            "Attach the file and check the duplicate warning before submitting.",
        ],
        links: [
            { label: "Upload Documents", route: "/upload-documents" },
            { label: "View Documents", route: "/view-documents" },
        ],
    },
    {
        id: "staging",
        title: "Process staged email attachments",
        owner: "Archivist",
        time: "3-10 min",
        steps: [
            "Open Staging Imports and run Update Email Searches when you expect new mail from the archive mailbox.",
            "Use View to inspect the attachment and message body before validating.",
            "Use Edit to correct lodge, document type, event type, file type, date, access, title, and description.",
            "For an email carrying several attachments, use Document: Multiple Documents and list each item under Multiple Documents with its own description and date where known.",
            "Keep secretary email templates simple: fixed labels such as Lodge, Document, Meeting date, Event type, Description, and Multiple Documents; values are the parts that change.",
            "Validate only when the metadata is ready, then upload the validated item into the main archive. Delete only failed imports, spam, or duplicates you have already checked.",
        ],
        links: [
            { label: "Staging Imports", route: "/staging-imports" },
            { label: "View Documents", route: "/view-documents" },
        ],
    },
    {
        id: "members-lodges",
        title: "Maintain members, lodges, and dues",
        owner: "Secretary or Admin",
        time: "Ongoing",
        steps: [
            "Open Resources and use the Members and Lodges areas for day-to-day records.",
            "Member profiles are split into Overview, Membership, Degrees, Offices, Meetings, Contact, and Spouse. Save from the member form after editing.",
            "Membership category and membership status are separate fields. A member can be Full Member, Affiliate, Honorary, etc. while separately being Active, Resigned, Deceased, and so on.",
            "Record all membership and degree dates. The current degree should be the highest current degree, so Master Mason does not also count as current Entered Apprentice or Fellow Craft.",
            "Use member photos where available so overview screens show quick visual thumbnails as well as names.",
            "Lodge dues are stored by year. Each year can have different rates, due dates, bank details, required payment references, and partial payment history.",
        ],
        links: [
            { label: "Resources", route: "/resources" },
        ],
    },
    {
        id: "rsvp",
        title: "Run a meeting RSVP",
        owner: "Secretary",
        time: "2-5 min",
        steps: [
            "Open Surveys, create a meeting attendance survey, and set the lodge, meeting date, closing date, invite email recipients, result email recipients, and a short token.",
            "Use only one open survey per token. Wait for an RSVP to close before reusing the same token for a new meeting.",
            "Send the public survey link and token to the lodge or WhatsApp group. Respondents do not need to log in.",
            "Respondents can change Yes to No, or No to Yes, by submitting again with the same identity details before the survey closes.",
            "Public results show totals. Logged-in managers can see the named RSVP list and review private responses.",
            "When the survey closes, the system sends the RSVP summary to the result recipients and keeps the archive blind copy in place.",
            "If a visitor belongs to a lodge not in the dropdown, they can choose Other and type the lodge or body name with constitution/order, for example Lodge Name No. 123 S.C.",
            "Review typed lodge/body suggestions in Admin before adding anything to the permanent lodge list.",
        ],
        links: [
            { label: "Surveys", route: "/surveys" },
            { label: "Admin Review", route: "/admin" },
        ],
    },
    {
        id: "public",
        title: "Publish public archive heritage",
        owner: "Archivist",
        time: "Review work",
        steps: [
            "Use the document viewer to tick public review destinations for records that are safe to show publicly.",
            "Open Research to review public bodies, queues, and published items.",
            "Publish a body only when the public page has enough context for visitors to understand what it represents.",
            "Publish individual items after checking that the title, source date, description, image, access level, and download state are appropriate.",
            "Featured cards prefer banners or standards, then logos. The displayed source date should describe the evidence source, such as a 1977 agenda or a dated photograph.",
            "Move a body or item back to draft when it should be hidden from public visitors while remaining in the archive.",
        ],
        links: [
            { label: "Research", route: "/research" },
            { label: "View Documents", route: "/view-documents" },
        ],
    },
    {
        id: "quality",
        title: "Find gaps and clean records",
        owner: "Archivist or Admin",
        time: "Batch work",
        steps: [
            "Use Analytics for visitor activity, archive coverage, map issues, download demand, and data quality gaps.",
            "Use Resources for member, lodge, temple, meeting, degree, office, and contact completeness.",
            "The map ignores invalid zero-zero coordinates. Improve records by adding the most specific reliable address, not only a city centre.",
            "Exports and PDFs should include dates for membership, degree, office, and lodge history instead of only the latest status.",
            "Correct source data first whenever a report looks wrong, then export again.",
        ],
        links: [
            { label: "Analytics", route: "/analytics" },
            { label: "Resources", route: "/resources" },
        ],
    },
    {
        id: "admin",
        title: "Admin and reference data",
        owner: "Admin",
        time: "As needed",
        steps: [
            "Use Admin for user roles, lookup values, controlled reference data, and review queues.",
            "Add new reference values only when they are genuinely reusable. Fix spelling and aliases rather than creating near-duplicates.",
            "Use edit for corrections and delete only for unused or clearly wrong reference data.",
            "For public heritage, prefer Move to Draft over deletion when the record should be hidden but retained.",
            "Review RSVP lodge suggestions before creating a new lodge/body record from typed public responses.",
        ],
        links: [
            { label: "Admin", route: "/admin" },
        ],
    },
];

const TOPICS = [
    {
        group: "Archive",
        title: "Documents",
        text: "Search and filter by title, description, lodge, event type, document type, submitter, uploader, access role, date, file type, public state, and review status.",
        route: "/view-documents",
    },
    {
        group: "Archive",
        title: "Uploads",
        text: "Use for clean final files. Check metadata, access roles, duplicate warnings, selected file, generated title, and description before submitting.",
        route: "/upload-documents",
    },
    {
        group: "Archive",
        title: "Staging imports",
        text: "Email imports are a review queue, not the permanent archive. Inspect, edit, validate, then upload. Multiple-document emails should describe each attachment separately.",
        route: "/staging-imports",
    },
    {
        group: "Archive",
        title: "Multiple documents",
        text: "Use the Multiple Documents section when one email carries minutes, reports, budgets, balance sheets, or other separate files. Each listed item should have its own type, description, and date where known.",
        route: "/staging-imports",
    },
    {
        group: "Archive",
        title: "Public review",
        text: "Queue public-safe items from the document viewer, then publish or move to draft from Research. Keep private minutes, personal details, and sensitive member records protected.",
        route: "/research",
    },
    {
        group: "Resources",
        title: "Temples",
        text: "Maintain building addresses, status, images, linked documents, lodge movement history, map data, and completeness notes.",
        route: "/resources",
    },
    {
        group: "Resources",
        title: "Lodges",
        text: "Review overview details, members, documents, meetings, notes, constitution, lodge type, status, consecration date, current temple, public heritage, and location history.",
        route: "/resources",
    },
    {
        group: "Resources",
        title: "Members",
        text: "Maintain core profile fields, photos, membership history, degrees, officer history, meetings, contact details, spouse details, linked records, and exports.",
        route: "/resources",
    },
    {
        group: "Resources",
        title: "Membership status",
        text: "Category and status are separate. Category is the member's type, such as Full Member or Honorary. Status is whether the membership is Active, Resigned, Deceased, Suspended, or similar.",
        route: "/resources",
    },
    {
        group: "Resources",
        title: "Degrees",
        text: "Record every degree with dates. Reports should show the highest current degree and the underlying Entered Apprentice, Fellow Craft, and Master Mason dates where available.",
        route: "/resources",
    },
    {
        group: "Resources",
        title: "Meetings",
        text: "Record meeting type, date, description, attendees, apologies, visitors, officers, linked documents, attendance exports, and RSVP history where applicable.",
        route: "/resources",
    },
    {
        group: "Resources",
        title: "Dues and payments",
        text: "Dues are yearly. Each lodge year can have different payable dates, annual rates, bank accounts, required references, notes, payments, partial payments, and outstanding balances.",
        route: "/resources",
    },
    {
        group: "Resources",
        title: "Charities and fundraising",
        text: "Capture donations, funds, events, fundraising activity, evidence documents, adverts, photos, targets, and contribution rankings.",
        route: "/resources",
    },
    {
        group: "Surveys",
        title: "Meeting RSVP",
        text: "Respondents use the public link and token. They do not need to log in, can change their answer before close, and can choose Other when their lodge is not listed.",
        route: "/surveys",
    },
    {
        group: "Surveys",
        title: "RSVP results",
        text: "Public results show counts. Logged-in managers can review the named response list, and close emails are sent automatically to the configured result recipients.",
        route: "/surveys",
    },
    {
        group: "Research",
        title: "Public lodge pages",
        text: "Public pages show published heritage first while detailed documents, event notes, private records, and member-level content remain behind login.",
        route: "/research",
    },
    {
        group: "Research",
        title: "Banners and logos",
        text: "Featured lodge cards prefer a published banner or standard image. If none exists, use a logo or fallback image. Image metadata should describe the source evidence and date.",
        route: "/research",
    },
    {
        group: "Admin",
        title: "User access",
        text: "Admin and Archivist users can update user roles. New users should register first, then be assigned the correct access level.",
        route: "/admin",
    },
    {
        group: "Admin",
        title: "Reference data",
        text: "Maintain controlled tables such as lodges, bodies, document types, event types, file types, roles, countries, constitutions, orders, statuses, and categories.",
        route: "/admin",
    },
    {
        group: "Admin",
        title: "Add, edit, delete",
        text: "Add when a value is reusable, edit when a record is wrong, and delete only when the item is unused or clearly invalid. For public archive content, move to draft when possible.",
        route: "/admin",
    },
    {
        group: "Admin",
        title: "RSVP lodge suggestions",
        text: "Typed Other lodge/body responses are collected for review. Check names and constitution/order before adding them to the permanent lodge list.",
        route: "/admin",
    },
    {
        group: "Analytics",
        title: "Archive intelligence",
        text: "Use analytics to find missing fields, needs-attention records, ranked entities, exportable tables, visitor patterns, map issues, and completeness gaps.",
        route: "/analytics",
    },
    {
        group: "Analytics",
        title: "Location map",
        text: "Zero-zero coordinates are invalid and should be ignored. Improve accuracy by adding reliable street, suburb, city, province, and country details where known.",
        route: "/analytics",
    },
];

const ROLE_GUIDES = [
    {
        role: "Secretary",
        focus: "Upload minutes, agendas, summonses, adverts, meeting packs, RSVP surveys, lodge dues, and member updates.",
        doFirst: "Start with Upload Documents for final records and Surveys for meeting attendance.",
    },
    {
        role: "Archivist",
        focus: "Clean metadata, manage staging imports, review descriptions, publish public heritage, and correct archive records.",
        doFirst: "Start with Staging Imports, View Documents, and public review queues.",
    },
    {
        role: "Researcher",
        focus: "Find public and protected evidence, follow research paths, and use archive context carefully.",
        doFirst: "Start in Research, then use View Documents for primary records.",
    },
    {
        role: "Admin",
        focus: "Maintain users, roles, lookup tables, RSVP lodge suggestions, controlled reference data, and quality settings.",
        doFirst: "Use Admin for roles, lookup maintenance, and review queues.",
    },
    {
        role: "Member",
        focus: "Use permitted archive views, RSVP surveys, calendars, resources, and research material.",
        doFirst: "Start with Surveys for responses and Research or View Documents for archive browsing.",
    },
];

function InstructionsPage() {
    const [query, setQuery] = useState("");
    const [activeWorkflow, setActiveWorkflow] = useState(WORKFLOWS[0].id);

    const selectedWorkflow = WORKFLOWS.find((workflow) => workflow.id === activeWorkflow) || WORKFLOWS[0];

    const filteredTopics = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return TOPICS;
        return TOPICS.filter((topic) =>
            [topic.group, topic.title, topic.text].join(" ").toLowerCase().includes(q)
        );
    }, [query]);

    return (
        <main className="instructions-centre">
            <section className="instructions-hero">
                <div>
                    <p className="instructions-eyebrow">Help centre</p>
                    <h1>Instructions</h1>
                    <p>
                        Practical guidance for using the Connaught archive: upload records, review staging imports,
                        maintain resources, manage access, and find useful evidence quickly.
                    </p>
                </div>
                <div className="instructions-search-card">
                    <label htmlFor="instruction-search">Find guidance</label>
                    <input
                        id="instruction-search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Search documents, lodges, meetings, roles..."
                    />
                    <span>{filteredTopics.length} matching topics</span>
                </div>
            </section>

            <section className="instructions-quick-grid" aria-label="Quick actions">
                {QUICK_ACTIONS.map((action) => (
                    <article className="instruction-action-card" key={action.title}>
                        <h2>{action.title}</h2>
                        <p>{action.description}</p>
                        <Link to={action.route}>{action.cta}</Link>
                    </article>
                ))}
            </section>

            <section className="instructions-two-column">
                <div className="instructions-panel">
                    <div className="instructions-panel-heading">
                        <p>Guided workflows</p>
                        <h2>Common Tasks</h2>
                    </div>
                    <div className="workflow-tabs" role="tablist" aria-label="Instruction workflows">
                        {WORKFLOWS.map((workflow) => (
                            <button
                                key={workflow.id}
                                type="button"
                                className={workflow.id === activeWorkflow ? "active" : ""}
                                onClick={() => setActiveWorkflow(workflow.id)}
                            >
                                {workflow.title}
                            </button>
                        ))}
                    </div>

                    <article className="workflow-detail">
                        <div className="workflow-meta">
                            <span>{selectedWorkflow.owner}</span>
                            <span>{selectedWorkflow.time}</span>
                        </div>
                        <h3>{selectedWorkflow.title}</h3>
                        <ol>
                            {selectedWorkflow.steps.map((step) => (
                                <li key={step}>{step}</li>
                            ))}
                        </ol>
                        <div className="workflow-links">
                            {selectedWorkflow.links.map((link) => (
                                <Link key={link.label} to={link.route}>
                                    {link.label}
                                </Link>
                            ))}
                        </div>
                    </article>
                </div>

                <aside className="instructions-panel">
                    <div className="instructions-panel-heading">
                        <p>By role</p>
                        <h2>What To Use</h2>
                    </div>
                    <div className="role-guide-list">
                        {ROLE_GUIDES.map((item) => (
                            <article key={item.role}>
                                <h3>{item.role}</h3>
                                <p>{item.focus}</p>
                                <strong>{item.doFirst}</strong>
                            </article>
                        ))}
                    </div>
                </aside>
            </section>

            <section className="instructions-panel">
                <div className="instructions-panel-heading split">
                    <div>
                        <p>Reference</p>
                        <h2>Feature Guide</h2>
                    </div>
                    {query && (
                        <button type="button" onClick={() => setQuery("")}>
                            Clear search
                        </button>
                    )}
                </div>

                <div className="topic-grid">
                    {filteredTopics.map((topic) => (
                        <article className="topic-card" key={`${topic.group}-${topic.title}`}>
                            <span>{topic.group}</span>
                            <h3>{topic.title}</h3>
                            <p>{topic.text}</p>
                            <Link to={topic.route}>Open section</Link>
                        </article>
                    ))}
                </div>

                {filteredTopics.length === 0 && (
                    <div className="instructions-empty">
                        No matching guidance found. Try searching for documents, uploads, meetings, members, staging, or admin.
                    </div>
                )}
            </section>
        </main>
    );
}

export default InstructionsPage;
